// src/features/analytics/utils/campaignActions.ts
// "Acciones inteligentes": traducen un hallazgo del análisis en una campaña
// pre-dirigida. Cada constructor devuelve un objeto `prefill` que se pasa por
// navegación a /dashboard/campaigns; ahí CampaignsPage abre el form de campaña
// NUEVA ya configurada (tipo + filtros + nombre + mensaje sugerido editable).
//
// Variables que el mensaje admite (las resuelve CampaignPreview por cliente):
//   @nombre  -> primer nombre del cliente
//   @puntos  -> saldo de puntos del cliente
//   @marca   -> nombre del negocio
//
// Targeting por FILTROS dinámicos (el backend resuelve el público), no por IDs
// estáticos: evita el límite de 1 MB de Firestore y se mantiene actualizado.

export type CampaignType = 'birthday' | 'inactive' | 'all' | 'custom';

export interface CampaignFilter { type: string; value: any; }

export interface CampaignPrefill {
  name: string;
  description: string;
  type: CampaignType;
  message: string;
  filters: CampaignFilter[];
  image: null;
  selectedClientIds?: string[];
}

export const RUTA_CAMPANAS = '/dashboard/campaigns';

const MSG_REACTIVACION = '¡Hola @nombre! 💜 Te extrañamos en @marca. Tienes @puntos puntos esperándote; pásate a usarlos en tu próxima visita. ¡Te esperamos!';

// Reactivar clientes que dejaron de venir.
// Si se pasan clientIds (segmento EXACTO del análisis) → campaña dirigida a ESOS clientes.
// Si no → fallback por filtro (alcance amplio: todos los inactivos 30+ días).
export const prefillReactivacion = (clientIds?: string[]): CampaignPrefill => {
  if (clientIds && clientIds.length > 0) {
    return {
      name: 'Reactivación de clientes',
      description: `${clientIds.length} clientes del segmento "en riesgo" del análisis`,
      type: 'custom',
      filters: [],
      selectedClientIds: clientIds,
      image: null,
      message: MSG_REACTIVACION,
    };
  }
  return {
    name: 'Reactivación de clientes',
    description: 'Todos los clientes que no han vuelto en 30+ días',
    type: 'inactive',
    filters: [{ type: 'inactiveDays', value: 30 }],
    image: null,
    message: MSG_REACTIVACION,
  };
};

// Reactivar el segmento de inactividad de N+ días (lo usa el panel "Clientes Inactivos").
// Targeting DINÁMICO por filtro (el backend resuelve el público y se mantiene fresco al enviar),
// no por IDs estáticos: el conjunto de inactivos puede ser grande.
export const prefillReactivacionDias = (inactiveDays: number): CampaignPrefill => ({
  name: `Reactivación (${inactiveDays}+ días)`,
  description: `Clientes sin venir hace ${inactiveDays}+ días`,
  type: 'inactive',
  filters: [{ type: 'inactiveDays', value: inactiveDays }],
  image: null,
  message: MSG_REACTIVACION,
});

// Reactivar una BANDA de inactividad (entre min y max días sin venir) — el público más "tibio".
// Campaña custom con recencyRange (el motor resuelve el público dinámicamente).
export const prefillReactivacionRango = (minDays: number, maxDays: number): CampaignPrefill => ({
  name: `Reactivación (${minDays}-${maxDays} días)`,
  description: `Clientes sin venir entre ${minDays} y ${maxDays} días`,
  type: 'custom',
  filters: [{ type: 'recencyRange', value: { min: minDays, max: maxDays } }],
  image: null,
  message: MSG_REACTIVACION,
});

// "Te extrañamos" a los FRECUENTES en riesgo (tarjeta del dashboard): banda de recencia
// + saldo mínimo (clientes que ya invirtieron en el programa — los más recuperables).
export const prefillReactivacionFrecuentes = (minDays: number, maxDays: number, minPoints: number): CampaignPrefill => ({
  name: 'Te extrañamos (frecuentes en riesgo)',
  description: `Clientes frecuentes (saldo ${minPoints}+) sin venir entre ${minDays} y ${maxDays} días`,
  type: 'custom',
  filters: [
    { type: 'recencyRange', value: { min: minDays, max: maxDays } },
    { type: 'minPoints', value: minPoints },
  ],
  image: null,
  message: MSG_REACTIVACION,
});

// Felicitar a los cumpleañeros del MES calendario (no ventana de 30 días).
// birthMonth se indexa 0-11; getMonth() del navegador también es 0-indexed.
export const prefillCumpleanos = (): CampaignPrefill => ({
  name: 'Felicitación de cumpleaños del mes',
  description: 'Clientes que cumplen años este mes calendario',
  type: 'custom',
  filters: [{ type: 'birthdayMonth', value: new Date().getMonth() }],
  image: null,
  message: '🎉 ¡Feliz cumpleaños, @nombre! En @marca queremos celebrarte: pásate este mes y disfruta de un detalle especial de nuestra parte. 🎂',
});

const MSG_VIP = '🌟 @nombre, eres de nuestros clientes más especiales. Gracias por tu preferencia en @marca. Tienes @puntos puntos: ¡pásate por algo especial!';

// Premiar / agradecer a los mejores clientes (Campeones).
// Con clientIds → campaña dirigida a los Campeones EXACTOS del análisis.
// Sin ellos → fallback por filtro (clientes con puntos altos).
export const prefillMejoresClientes = (clientIds?: string[]): CampaignPrefill => {
  if (clientIds && clientIds.length > 0) {
    return {
      name: 'Recompensa a mejores clientes',
      description: `${clientIds.length} "Campeones" del análisis`,
      type: 'custom',
      filters: [],
      selectedClientIds: clientIds,
      image: null,
      message: MSG_VIP,
    };
  }
  return {
    name: 'Recompensa a mejores clientes',
    description: 'Clientes con saldo alto de puntos',
    type: 'custom',
    filters: [{ type: 'minPoints', value: 50 }],
    image: null,
    message: MSG_VIP,
  };
};

// Impulsar el canje de un premio poco usado.
export const prefillPromoPremio = (premio: string): CampaignPrefill => ({
  name: premio ? `Promoción: ${premio}` : 'Promoción de premio',
  description: 'Impulsar el canje de un premio',
  type: 'all',
  filters: [],
  image: null,
  message: `🎁 ¡@nombre! ¿Ya conoces "${premio || 'nuestros premios'}" en @marca? Tienes @puntos puntos; pásate a canjearlo. ¡Te va a encantar!`,
});

// Recordar a los clientes que tienen puntos sin canjear (modelo puntos-canje).
export const prefillRecordarPuntos = (minPoints = 10): CampaignPrefill => ({
  name: 'Recordatorio de puntos',
  description: 'Clientes con puntos sin canjear',
  type: 'custom',
  filters: [{ type: 'minPoints', value: minPoints }],
  image: null,
  message: '💰 @nombre, tienes @puntos puntos acumulados en @marca esperando a ser canjeados. ¡No los dejes ir, pásate pronto!',
});
